import React, { useEffect, useState } from "react";
import { FaRegQuestionCircle } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Divider, Grid, makeStyles, Typography, Tooltip } from "@material-ui/core";

import { header } from "../../sample";
import { ClusterComponent } from "./cluster";
import { COLORS } from "../../../../../theme/colors";
import { ShaDowBox } from "../../../../../components/boxes";
import endPoints from "../../../../../services/apiEndPoints";
import { DiscountRateModal } from "./modals/discountRateModal";
import { getData, getErrorMessage, postData } from "../../../../../services";
import { RedAsterisk } from "../../../../../components/characters";
import { PortfolioSaveSuccessModal } from "./modals/saveSucessModal";
import SettingHeader from "../../../../../components/SettingHeader";
import discountIcon from "../../../../../assets/images/icons/discount.svg";
import { SettingActions } from "../../../../../components/SettingHeader/settingActions";
import { Button, Icon, TextButton, TextInput } from "../../../../../components/base";
import { saveNotification } from "../../../../../redux/notification/actions";
import { setCSC } from "../../../../../redux/auth/actions";

const useStyles = makeStyles((theme) => ({
  title: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
  },
  marcoRow: {
    marginLeft: theme.spacing(3),
  },
}));

export const PortfolioProfileTab = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { cluster } = useSelector((state) => state.selectCSC);

  const [clusterRowCount, setClusterRowCount] = useState(1);
  const [discount, setDiscount] = useState();
  const [clusters, setClusters] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [capitalizationRate, setCapitalizationRate] = useState();
  const [initialDiscountRate, setInitialDiscountRate] = useState();
  const [openDiscountModal, setOpenDiscountModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (cluster) {
      getData(endPoints.SETTING_PORTFOLIO_PROFILE, (data) => {
        saveInitData(data);
      });
    }
  }, [cluster]);

  useEffect(() => {
    if (buildings.length === 0) {
      return;
    }
    buildings.forEach((b) => {
      for (const c of clusters) {
        if (!c.id) continue;
        for (const cBuilding of c.buildings) {
          if (b.id === cBuilding.id) {
            b["in_cluster"] = {
              id: c.id,
              name: c.name,
            };
            break;
          }
        }
        if (b["in_cluster"]) break;
      }
    });
    setBuildings([...buildings]);
  }, [clusters]);

  const saveInitData = (data) => {
    setBuildings(data.buildings || []);
    setClusters(data.clusters || []);
    setCapitalizationRate(data.cap_rate || undefined);
    setInitialDiscountRate(data.discount);
    setClusterRowCount(data.clusters?.length || 1);
  };

  const onClusterDataChange = (index, key, value) => {
    clusters[index] = { ...(clusters[index] || {}), [key]: value };
    setClusters([...clusters]);
  };

  const removeCluster = (building) => {
    const newBuildings = buildings.map((b) => {
      if (b.id === building.id) {
        b["in_cluster"] = null;
      }
      return b;
    });
    setBuildings(newBuildings);
  };

  const onSave = () => {
    if (saving) return;
    setSaving(true);
    setErrors({});
    postData(
      endPoints.SETTING_PORTFOLIO_PROFILE,
      (data) => {
        saveInitData(data);
        setOpenSuccessModal(true);
        getData("/auth/csc/", (data) => dispatch(setCSC(data)));
      },
      {
        clusters: clusters.map((c) => ({
          id: c.id,
          name: c.name,
          pm: c.pm_data && c.pm_data?.id !== user.id ? c.pm_data : undefined,
          building_ids: c.buildings?.map((b) => b.id),
        })),
        cap_rate: capitalizationRate,
        discount: discount,
      }
    )
      .catch((e) => {
        setErrors(e.response.data);
        dispatch(saveNotification({ message: getErrorMessage(e), error: true }));
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <SettingActions />
      <ShaDowBox style={{ padding: "40px 50px" }}>
        <SettingHeader
          title={header.portfolioProfile.title}
          subtitle={header.portfolioProfile.subtitle}
          img={header.portfolioProfile.img}
        />
        <Grid container justifyContent="flex-end">
          <Button text="Save" loading={saving} onClick={onSave} />
        </Grid>
        <Grid className={classes.title} container alignItems="center" spacing={1}>
          <RedAsterisk />
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            Portfolio Clusters
          </Typography>
          <Tooltip
            arrow
            title="Clusters reflect the business organisational structure. Clusters can be on regional basis, portfolio unit, internal brand etc"
          >
            <div style={{ paddingTop: 4 }}>
              <FaRegQuestionCircle color={COLORS.grey} style={{ marginLeft: 6 }} />
            </div>
          </Tooltip>
        </Grid>
        {Array.from({ length: clusterRowCount }, (_, i) => i).map((index) => (
          <React.Fragment key={index}>
            <ClusterComponent
              index={index}
              data={clusters[index] || {}}
              onClusterDataChange={onClusterDataChange}
              buildings={buildings}
              removeCluster={removeCluster}
              errors={(errors.clusters || [])[index]}
            />
            {index !== clusterRowCount - 1 && <Divider />}
          </React.Fragment>
        ))}
        <TextButton
          text="+ Add Cluster"
          normalFontWeight
          onClick={() => setClusterRowCount(clusterRowCount + 1)}
        />

        <Divider style={{ margin: "16px 0px" }} />

        {/* Macro PRaaS Assumptions */}
        <Grid className={classes.title} container alignItems="center" spacing={1}>
          <RedAsterisk />
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            Macro PRaaS Assumptions
          </Typography>
        </Grid>
        <Grid className={classes.marcoRow} container alignItems="center">
          <Grid item xs={3}>
            <Typography>Target capitalization rate:</Typography>
          </Grid>
          <Grid item xs={3}>
            <TextInput
              value={capitalizationRate}
              onChange={setCapitalizationRate}
              justifyContent="flex-start"
              suffix="%"
              type="number"
            />
          </Grid>
        </Grid>
        <Grid className={classes.marcoRow} container alignItems="center">
          <Grid item xs={3}>
            <Typography>Discount rates:</Typography>
          </Grid>
          <Grid item xs={4}>
            <TextButton
              icon={<Icon src={discountIcon} style={{ marginRight: 4 }} />}
              text="Set discount rates table"
              normalFontWeight
              onClick={() => setOpenDiscountModal(true)}
            />
          </Grid>
        </Grid>
      </ShaDowBox>

      <DiscountRateModal
        initialDiscounts={initialDiscountRate || []}
        setInitialDiscountRate={setInitialDiscountRate}
        open={openDiscountModal}
        onClose={() => setOpenDiscountModal(false)}
        onSave={(data) => setDiscount(data)}
      />
      <PortfolioSaveSuccessModal
        open={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      />
    </>
  );
};
